import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '@/api';
import {
  Shield, Mail, Loader2, FileText, AlertTriangle, CheckCircle2,
  Layers, ClipboardList, HelpCircle, Package, MessageSquare,
  Edit3, X, ChevronDown, Send
} from 'lucide-react';
import MessageThread from '../../components/MessageThread';

const SECTION_ICONS = {
  submittals: ClipboardList,
  rfis: HelpCircle,
  sheets: Layers,
  assets: Package,
};

function SectionCard({ title, icon: Icon, items, columns, emptyMsg, onAskQuestion, onProposeUpdate }) {
  const [expanded, setExpanded] = useState(items.length > 0);
  return (
    <div className="border border-border rounded-sm bg-card" data-testid={`portal-section-${title.toLowerCase()}`}>
      <button onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/20 transition-colors">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-primary/70" />
          <span className="text-sm font-medium text-foreground">{title}</span>
          <span className="text-[10px] font-mono text-muted-foreground bg-secondary/50 px-1.5 py-0.5 rounded-sm border border-border/50">
            {items.length}
          </span>
        </div>
        <span className="text-[10px] text-muted-foreground">{expanded ? 'Collapse' : 'Expand'}</span>
      </button>
      {expanded && (
        <div className="border-t border-border/50">
          {items.length > 0 ? (
            <div className="divide-y divide-border/30">
              {/* Header */}
              <div className="flex items-center gap-3 px-4 py-1.5 bg-muted/10">
                {columns.map(col => (
                  <span key={col.key} className={`text-[10px] font-heading uppercase tracking-widest text-muted-foreground ${col.className || 'flex-1'}`}>
                    {col.label}
                  </span>
                ))}
                {(onAskQuestion || onProposeUpdate) && <span className="text-[10px] w-20 shrink-0"></span>}
              </div>
              {items.map((item, i) => (
                <div key={item.id || i} className="flex items-center gap-3 px-4 py-2 hover:bg-muted/10 transition-colors group">
                  {columns.map(col => (
                    <span key={col.key} className={`text-xs text-foreground/80 ${col.className || 'flex-1'} truncate`}>
                      {col.render ? col.render(item) : (item[col.key] || '—')}
                    </span>
                  ))}
                  <div className="w-20 shrink-0 flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {onProposeUpdate && (
                      <button onClick={() => onProposeUpdate(item)}
                        data-testid={`propose-update-${item.id || i}`}
                        title="Propose attribute update"
                        className="p-1 rounded-sm hover:bg-amber-100 dark:hover:bg-amber-950/30 transition-colors">
                        <Edit3 className="w-3.5 h-3.5 text-amber-500 hover:text-amber-400" />
                      </button>
                    )}
                    {onAskQuestion && (
                      <button onClick={() => onAskQuestion(item)}
                        data-testid={`ask-question-${item.id || i}`}
                        title="Ask a question"
                        className="p-1 rounded-sm hover:bg-blue-100 dark:hover:bg-blue-950/30 transition-colors">
                        <MessageSquare className="w-3.5 h-3.5 text-blue-400 hover:text-blue-300" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-4 py-6 text-center text-xs text-muted-foreground">{emptyMsg}</div>
          )}
        </div>
      )}
    </div>
  );
}

export default function TradePortal() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [data, setData] = useState(null);
  const [email, setEmail] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalMessages, setPortalMessages] = useState([]);
  const [msgName, setMsgName] = useState('');
  const [msgEmail, setMsgEmail] = useState('');
  const [msgItemType, setMsgItemType] = useState(null);
  const [msgItemId, setMsgItemId] = useState(null);
  const [msgItemLabel, setMsgItemLabel] = useState('');
  const [proposalAsset, setProposalAsset] = useState(null);
  const [proposalFields, setProposalFields] = useState({});
  const [proposalNotes, setProposalNotes] = useState('');
  const [submittingProposal, setSubmittingProposal] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [showProposals, setShowProposals] = useState(false);

  // Fetch portal info on mount
  useEffect(() => {
    api.get(`/trade-portal/${token}`)
      .then(r => {
        setInfo(r.data);
        if (r.data.verified) {
          // Already verified, fetch data
          fetchData();
        }
      })
      .catch(err => {
        setError(err.response?.data?.detail || 'Invalid or expired portal link');
      })
      .finally(() => setLoading(false));
  }, [token]);

  const fetchData = async () => {
    try {
      const r = await api.get(`/trade-portal/${token}/data`);
      setData(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load portal data');
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setVerifying(true);
    setError(null);
    try {
      await api.post(`/trade-portal/${token}/verify`, { email });
      setInfo(prev => ({ ...prev, verified: true }));
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Email verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const fetchPortalMessages = useCallback(async () => {
    try {
      const r = await api.get(`/trade-portal/${token}/messages`);
      setPortalMessages(r.data || []);
    } catch {}
  }, [token]);

  useEffect(() => {
    if (data) {
      fetchPortalMessages();
      const interval = setInterval(fetchPortalMessages, 10000);
      return () => clearInterval(interval);
    }
  }, [data, fetchPortalMessages]);

  const handleSendPortalMessage = async (text) => {
    await api.post(`/trade-portal/${token}/messages`, {
      text,
      sender_name: msgName,
      sender_email: msgEmail || email,
      item_type: msgItemType,
      item_id: msgItemId,
      item_label: msgItemLabel,
    });
    await fetchPortalMessages();
    // Reset item context after sending
    setMsgItemType(null);
    setMsgItemId(null);
    setMsgItemLabel('');
  };

  const startItemMessage = (type, id, label) => {
    setMsgItemType(type);
    setMsgItemId(id);
    setMsgItemLabel(label);
    // Scroll to message thread
    setTimeout(() => {
      document.querySelector('[data-testid="message-thread"]')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const fetchProposals = useCallback(async () => {
    try {
      const r = await api.get(`/trade-portal/${token}/proposals`);
      setProposals(r.data || []);
    } catch {}
  }, [token]);

  useEffect(() => {
    if (data) fetchProposals();
  }, [data, fetchProposals]);

  const handleSubmitProposal = async () => {
    if (!proposalAsset) return;
    setSubmittingProposal(true);
    try {
      const changes = {};
      for (const [key, val] of Object.entries(proposalFields)) {
        if (key === 'attributes') {
          const attrs = {};
          for (const [ak, av] of Object.entries(val || {})) {
            if (av && String(av).trim()) attrs[ak] = String(av).trim();
          }
          if (Object.keys(attrs).length > 0) changes.attributes = attrs;
        } else if (val && String(val).trim()) {
          changes[key] = String(val).trim();
        }
      }
      if (Object.keys(changes).length === 0) {
        setSubmittingProposal(false);
        return;
      }
      await api.post(`/trade-portal/${token}/proposals`, {
        asset_id: proposalAsset.id,
        proposed_changes: changes,
        notes: proposalNotes,
      });
      setProposalAsset(null);
      setProposalFields({});
      setProposalNotes('');
      fetchProposals();
    } catch (err) {
      console.error(err);
    } finally {
      setSubmittingProposal(false);
    }
  };

  const startProposal = (asset) => {
    setProposalAsset(asset);
    setProposalFields({
      make: asset.manufacturer || '',
      model: asset.model || '',
      location: asset.location || '',
      attributes: {},
    });
    setProposalNotes('');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error && !info) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-sm">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-lg font-medium text-foreground mb-2">Portal Unavailable</h1>
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  // Email verification screen
  if (info && !info.verified) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="w-full max-w-md" data-testid="portal-verify">
          <div className="text-center mb-8">
            <Shield className="w-12 h-12 text-primary mx-auto mb-4" />
            <h1 className="text-xl font-medium text-foreground mb-1">Trade Portal Access</h1>
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{info.project_name}</span> — {info.trade}
            </p>
          </div>

          <form onSubmit={handleVerify} className="bg-card border border-border rounded-sm p-6 space-y-4">
            <div>
              <label className="text-xs font-heading uppercase tracking-widest text-muted-foreground block mb-1.5">
                Verify Your Email
              </label>
              <p className="text-[10px] text-muted-foreground mb-3">
                Enter your email address to verify access. Your email must match the invitation or be listed as a project team member.
              </p>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-muted-foreground shrink-0" />
                <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="your@company.com" data-testid="portal-email-input"
                  className="flex-1 px-3 py-2 text-sm bg-secondary border border-border rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50" />
              </div>
            </div>
            {error && (
              <div className="flex items-center gap-2 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/30 rounded-sm p-2" data-testid="portal-verify-error">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                {error}
              </div>
            )}
            <button type="submit" disabled={verifying} data-testid="portal-verify-btn"
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-heading uppercase tracking-wider bg-primary text-primary-foreground rounded-sm hover:bg-primary/90 transition-colors disabled:opacity-50">
              {verifying ? <><Loader2 className="w-4 h-4 animate-spin" /> Verifying...</> : <><Shield className="w-4 h-4" /> Verify & Access Portal</>}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Verified — show trade-scoped data
  return (
    <div className="min-h-screen bg-background" data-testid="trade-portal-data">
      {/* Header */}
      <div className="border-b border-border bg-card px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-lg font-medium text-foreground">{data?.project_name || info?.project_name}</h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              Trade Portal — <span className="font-medium text-foreground">{data?.trade || info?.trade}</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            <span className="text-xs text-emerald-600 dark:text-emerald-400">Verified Access</span>
          </div>
        </div>
      </div>

      {/* Summary bar */}
      {data?.summary && (
        <div className="border-b border-border/50 bg-muted/10 px-6 py-3">
          <div className="max-w-5xl mx-auto flex items-center gap-6">
            {[
              { label: 'Submittals', count: data.summary.submittals_count },
              { label: 'RFIs', count: data.summary.rfis_count },
              { label: 'Drawings', count: data.summary.sheets_count },
              { label: 'Assets', count: data.summary.assets_count },
            ].map(s => (
              <div key={s.label} className="text-center">
                <div className="text-lg font-medium text-foreground">{s.count}</div>
                <div className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 py-6 space-y-4">
        {data ? (
          <>
            <SectionCard
              title="Submittals" icon={SECTION_ICONS.submittals}
              items={data.submittals}
              emptyMsg={`No submittals found for ${data.trade}`}
              onAskQuestion={(item) => startItemMessage('submittal', item.id, item.description || `Submittal ${item.spec_section || ''}`)}
              columns={[
                { key: 'spec_section', label: 'Spec', className: 'w-20 shrink-0' },
                { key: 'description', label: 'Description' },
                { key: 'manufacturer', label: 'Manufacturer', className: 'w-32 shrink-0' },
                { key: 'status', label: 'Status', className: 'w-24 shrink-0',
                  render: item => (
                    <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${
                      item.status === 'approved' ? 'text-emerald-700 bg-emerald-100 border-emerald-300 dark:text-emerald-300 dark:bg-emerald-950/30 dark:border-emerald-800' :
                      item.status === 'pending' ? 'text-amber-700 bg-amber-100 border-amber-300 dark:text-amber-300 dark:bg-amber-950/30 dark:border-amber-800' :
                      'text-gray-600 bg-gray-100 border-gray-300 dark:text-gray-400 dark:bg-gray-800/30 dark:border-gray-700'
                    }`}>{item.status || '—'}</span>
                  ),
                },
              ]}
            />
            <SectionCard
              title="RFIs" icon={SECTION_ICONS.rfis}
              items={data.rfis}
              emptyMsg={`No RFIs found for ${data.trade}`}
              onAskQuestion={(item) => startItemMessage('rfi', item.id, item.subject || `RFI #${item.rfi_number || ''}`)}
              columns={[
                { key: 'rfi_number', label: '#', className: 'w-16 shrink-0' },
                { key: 'subject', label: 'Subject' },
                { key: 'status', label: 'Status', className: 'w-24 shrink-0',
                  render: item => (
                    <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${
                      item.status === 'answered' ? 'text-emerald-700 bg-emerald-100 border-emerald-300 dark:text-emerald-300 dark:bg-emerald-950/30 dark:border-emerald-800' :
                      'text-amber-700 bg-amber-100 border-amber-300 dark:text-amber-300 dark:bg-amber-950/30 dark:border-amber-800'
                    }`}>{item.status || 'open'}</span>
                  ),
                },
                { key: 'priority', label: 'Priority', className: 'w-16 shrink-0' },
              ]}
            />
            <SectionCard
              title="Drawing Sheets" icon={SECTION_ICONS.sheets}
              items={data.sheets}
              emptyMsg={`No drawing sheets found for ${data.trade}`}
              columns={[
                { key: 'sheet_number', label: 'Sheet #', className: 'w-20 shrink-0' },
                { key: 'sheet_name', label: 'Name' },
                { key: 'page_type', label: 'Type', className: 'w-24 shrink-0' },
                { key: 'discipline', label: 'Discipline', className: 'w-24 shrink-0' },
              ]}
            />
            <SectionCard
              title="Assets" icon={SECTION_ICONS.assets}
              items={data.assets}
              emptyMsg={`No assets found for ${data.trade}`}
              onAskQuestion={(item) => startItemMessage('asset', item.id, item.name || item.equipment_type || 'Asset')}
              onProposeUpdate={(item) => startProposal(item)}
              columns={[
                { key: 'name', label: 'Name' },
                { key: 'category', label: 'Category', className: 'w-28 shrink-0' },
                { key: 'manufacturer', label: 'Manufacturer', className: 'w-32 shrink-0' },
                { key: 'model', label: 'Model', className: 'w-32 shrink-0' },
                { key: 'location', label: 'Location', className: 'w-28 shrink-0' },
              ]}
            />

            {/* Proposal Form Modal */}
            {proposalAsset && (
              <div className="border border-amber-300 dark:border-amber-800/40 rounded-sm bg-amber-50/50 dark:bg-amber-950/10 p-4 space-y-3" data-testid="proposal-form">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Edit3 className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                    <span className="text-xs font-heading uppercase tracking-wider text-amber-700 dark:text-amber-400">
                      Propose Update — {proposalAsset.name || proposalAsset.category || 'Asset'}
                    </span>
                  </div>
                  <button onClick={() => setProposalAsset(null)} className="text-muted-foreground hover:text-foreground">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-[10px] text-muted-foreground">
                  Your proposed changes will be sent to the Project Manager for review and approval.
                </p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Manufacturer</label>
                    <input type="text" value={proposalFields.make || ''} onChange={e => setProposalFields(p => ({ ...p, make: e.target.value }))}
                      placeholder="e.g. Trane" data-testid="proposal-make"
                      className="mt-1 w-full px-2 py-1.5 text-xs bg-background border border-border rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50" />
                  </div>
                  <div>
                    <label className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Model</label>
                    <input type="text" value={proposalFields.model || ''} onChange={e => setProposalFields(p => ({ ...p, model: e.target.value }))}
                      placeholder="e.g. RTU-50XC" data-testid="proposal-model"
                      className="mt-1 w-full px-2 py-1.5 text-xs bg-background border border-border rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50" />
                  </div>
                  <div>
                    <label className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Location</label>
                    <input type="text" value={proposalFields.location || ''} onChange={e => setProposalFields(p => ({ ...p, location: e.target.value }))}
                      placeholder="e.g. Roof Level" data-testid="proposal-location"
                      className="mt-1 w-full px-2 py-1.5 text-xs bg-background border border-border rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50" />
                  </div>
                </div>
                {/* Attribute fields */}
                <div>
                  <label className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">
                    Sizing Attributes (add key-value pairs)
                  </label>
                  <div className="mt-1 space-y-1">
                    {Object.entries(proposalFields.attributes || {}).map(([key, val]) => (
                      <div key={key} className="flex items-center gap-2">
                        <input type="text" value={key} disabled
                          className="w-28 px-2 py-1 text-[10px] font-mono bg-muted border border-border rounded-sm text-muted-foreground" />
                        <input type="text" value={val} onChange={e => setProposalFields(p => ({
                          ...p, attributes: { ...p.attributes, [key]: e.target.value }
                        }))}
                          className="flex-1 px-2 py-1 text-[10px] font-mono bg-background border border-border rounded-sm text-foreground focus:outline-none focus:border-primary/50" />
                        <button onClick={() => setProposalFields(p => {
                          const newAttrs = { ...p.attributes };
                          delete newAttrs[key];
                          return { ...p, attributes: newAttrs };
                        })} className="text-muted-foreground hover:text-red-400">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    <button onClick={() => {
                      const key = prompt('Attribute name (e.g. btu, voltage, hp, tons, cfm):');
                      if (key && key.trim()) {
                        setProposalFields(p => ({
                          ...p, attributes: { ...p.attributes, [key.trim().toLowerCase()]: '' }
                        }));
                      }
                    }} className="text-[10px] text-amber-600 dark:text-amber-400 hover:underline"
                      data-testid="add-attr-btn">
                      + Add attribute
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Notes for PM</label>
                  <textarea value={proposalNotes} onChange={e => setProposalNotes(e.target.value)}
                    rows={2} placeholder="Any context for the project manager..."
                    data-testid="proposal-notes"
                    className="mt-1 w-full px-2 py-1.5 text-xs bg-background border border-border rounded-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50 resize-none" />
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={handleSubmitProposal} disabled={submittingProposal}
                    data-testid="submit-proposal-btn"
                    className="flex items-center gap-1.5 px-4 py-2 text-xs font-heading uppercase tracking-wider bg-amber-600 hover:bg-amber-500 text-white rounded-sm transition-colors disabled:opacity-50">
                    {submittingProposal ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    Submit Proposal
                  </button>
                  <button onClick={() => setProposalAsset(null)} className="px-3 py-2 text-xs text-muted-foreground">Cancel</button>
                </div>
              </div>
            )}

            {/* My Proposals */}
            {proposals.length > 0 && (
              <div className="border border-border rounded-sm bg-card" data-testid="my-proposals">
                <button onClick={() => setShowProposals(!showProposals)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/20 transition-colors">
                  <div className="flex items-center gap-2">
                    <Edit3 className="w-4 h-4 text-amber-500" />
                    <span className="text-sm font-medium text-foreground">My Proposals</span>
                    <span className="text-[10px] font-mono text-muted-foreground bg-secondary/50 px-1.5 py-0.5 rounded-sm border border-border/50">
                      {proposals.length}
                    </span>
                    {proposals.some(p => p.status === 'pending') && (
                      <span className="text-[9px] font-mono px-1.5 py-0.5 rounded-sm bg-amber-100 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 border border-amber-300 dark:border-amber-800/30">
                        {proposals.filter(p => p.status === 'pending').length} pending
                      </span>
                    )}
                  </div>
                  <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${showProposals ? 'rotate-180' : ''}`} />
                </button>
                {showProposals && (
                  <div className="border-t border-border/50 divide-y divide-border/30">
                    {proposals.map(p => (
                      <div key={p.id} className="px-4 py-2.5" data-testid={`proposal-${p.id}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-foreground">{p.asset_name || 'Asset'}</span>
                          <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded-sm border ${
                            p.status === 'approved' ? 'text-emerald-700 bg-emerald-100 border-emerald-300 dark:text-emerald-300 dark:bg-emerald-950/30 dark:border-emerald-800' :
                            p.status === 'rejected' ? 'text-red-700 bg-red-100 border-red-300 dark:text-red-300 dark:bg-red-950/30 dark:border-red-800' :
                            'text-amber-700 bg-amber-100 border-amber-300 dark:text-amber-300 dark:bg-amber-950/30 dark:border-amber-800'
                          }`}>{p.status}</span>
                        </div>
                        <div className="flex gap-2 flex-wrap text-[10px] text-muted-foreground">
                          {Object.entries(p.proposed_changes || {}).map(([k, v]) => (
                            <span key={k} className="font-mono bg-secondary/50 px-1 rounded border border-border/50">
                              {k}: {typeof v === 'object' ? JSON.stringify(v) : v}
                            </span>
                          ))}
                        </div>
                        {p.pm_notes && (
                          <p className="text-[10px] text-muted-foreground mt-1 italic">PM: {p.pm_notes}</p>
                        )}
                        <p className="text-[9px] text-muted-foreground/60 mt-0.5">
                          {new Date(p.created_at).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        )}

        {/* Message Thread */}
        {data && (
          <div className="border border-border rounded-sm bg-card overflow-hidden" data-testid="portal-messaging">
            {msgItemLabel && (
              <div className="px-4 py-2 bg-blue-500/10 border-b border-blue-500/20 flex items-center justify-between">
                <span className="text-[10px] text-blue-400 font-mono">
                  Asking about: <strong>{msgItemLabel}</strong>
                </span>
                <button onClick={() => { setMsgItemType(null); setMsgItemId(null); setMsgItemLabel(''); }}
                  className="text-[10px] text-blue-400 hover:text-blue-300 underline">Clear</button>
              </div>
            )}
            <MessageThread
              messages={portalMessages}
              onSend={handleSendPortalMessage}
              senderRole="sub"
              placeholder={msgItemLabel ? `Ask about ${msgItemLabel}...` : 'Send a message to the project manager...'}
              showIdentity={true}
              name={msgName}
              email={msgEmail || email}
              onNameChange={setMsgName}
              onEmailChange={setMsgEmail}
            />
          </div>
        )}
      </div>
    </div>
  );
}
